import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { desktop } from '../lib/native';
import { session } from '../lib/session';
import { targetLabel, updateSettings, useStore } from '../store';
import { Field, Toggle } from './ui';

type Iface = { name: string; channels: number; error?: string };

/** Mono channels 1…n, then odd-numbered stereo pairs. */
function outputOptions(n: number): [string, string][] {
  const opts: [string, string][] = [['main', 'Main mix (1–2)']];
  for (let c = 1; c <= n; c++) opts.push([`m:${c}`, `Ch ${c}`]);
  for (let c = 1; c + 1 <= n; c += 2) opts.push([`s:${c}`, `Ch ${c}–${c + 1} stereo`]);
  return opts;
}

/**
 * Desktop app only: take the mic from any channel of an audio interface, and send each key's audio
 * to its own output channel(s). Dante Virtual Soundcard and Dante Via appear as ordinary
 * multichannel devices, so this is how the app patches into a Dante network.
 */
export function InterfaceSettings() {
  const bridge = desktop();
  const settings = useStore((s) => s.settings);
  const panel = useStore((s) => s.panel);
  const media = useStore((s) => s.media);
  const [ifaces, setIfaces] = useState<Iface[] | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [outCount, setOutCount] = useState(2);

  const scan = useCallback(() => {
    if (!bridge) return;
    setIfaces(null);
    bridge.listInterfaces().then(setIfaces, () => setIfaces([]));
  }, [bridge]);

  useEffect(scan, [scan]);
  useEffect(() => bridge?.onCaptureError(setCaptureError), [bridge]);
  useEffect(() => setOutCount(session.audio.outputChannelCount()), [settings.outputDeviceId, settings.multichannelOut, media]);

  if (!bridge) return null;

  const micValue = settings.micInterface ? `${settings.micInterface.channel}\u0000${settings.micInterface.device}` : '';
  const setMic = (v: string) => {
    setCaptureError(null);
    if (!v) updateSettings({ micInterface: null });
    else {
      const [ch, ...rest] = v.split('\u0000');
      updateSettings({ micInterface: { device: rest.join('\u0000'), channel: Number(ch) } });
    }
    void session.restartMic();
  };

  const opts = outputOptions(outCount);
  const talkKeys = (panel?.keys ?? []).filter((k) => k.listen !== 'none');

  return (
    <>
      <div className="section-title">Audio interface · Dante</div>
      <p className="field__help">
        Dante Virtual Soundcard, Dante Via and other multichannel interfaces appear here. Take your mic from any input channel, and send each key to its own output
        channels.
      </p>

      <Field label="Microphone channel" help="Overrides the input device above. No echo cancellation or noise suppression is applied to interface channels.">
        <select className="select" value={micValue} onChange={(e) => setMic(e.target.value)} disabled={!ifaces}>
          <option value="">Use the microphone selected above</option>
          {(ifaces ?? []).map((d) =>
            d.error || d.channels < 1 ? null : (
              <optgroup key={d.name} label={`${d.name} · ${d.channels} ch`}>
                {Array.from({ length: d.channels }, (_, i) => (
                  <option key={i} value={`${i + 1}\u0000${d.name}`}>
                    {d.name} · Ch {i + 1}
                  </option>
                ))}
              </optgroup>
            ),
          )}
          {settings.micInterface && !ifaces?.some((d) => d.name === settings.micInterface?.device) && (
            <option value={micValue}>
              {settings.micInterface.device} · Ch {settings.micInterface.channel} (not found)
            </option>
          )}
        </select>
      </Field>
      {captureError && <p className="field__help" style={{ color: 'var(--error-400)' }}>{captureError}</p>}
      <button className="btn btn--neutral btn--block" onClick={scan} disabled={!ifaces} style={{ marginTop: 4 }}>
        <RefreshCw size={16} /> {ifaces ? 'Rescan interfaces' : 'Scanning interfaces…'}
      </button>

      <Toggle
        label="Route keys to separate output channels"
        help={`Choose the interface as the Output device above, then pick a channel for each key. This device has ${outCount} output channels.`}
        on={settings.multichannelOut}
        onChange={(v) => {
          updateSettings({ multichannelOut: v });
          void session.applyAudioSettings();
        }}
      />
      {settings.multichannelOut &&
        (outCount <= 2 ? (
          <p className="field__help">The current output device only has 2 channels. Select a multichannel device (e.g. Dante Virtual Soundcard) as the Output device.</p>
        ) : (
          talkKeys.map((k) => (
            <Field key={k.id} label={k.label ?? k.targets.map((t) => targetLabel(t, true)).join(' + ')}>
              <select
                className="select"
                value={settings.outputMap[k.id] ?? 'main'}
                onChange={(e) => {
                  const outputMap = { ...settings.outputMap };
                  if (e.target.value === 'main') delete outputMap[k.id];
                  else outputMap[k.id] = e.target.value;
                  updateSettings({ outputMap });
                  session.refreshGains();
                }}
              >
                {opts.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
          ))
        ))}
    </>
  );
}
